import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Receipt } from '@mui/icons-material'
import React from 'react'
import ReactPaginate from 'react-paginate'
import { pagedList } from 'src/viewModels/pagedList'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { GetInvoiceLogByInvoiceId } from 'src/services/QuoteService'
import { InvoiceLogSearchRequest } from 'src/viewModels/quotes/InvoiceLogSearchRequest'
import { InvoiceLogDTO } from 'src/viewModels/quotes/InvoiceLogDTO'
import moment from 'moment'
import { formatCurrency } from 'src/utils/Formatter'

interface IGlolbalInvoiceLogModalProps {
  invoiceId: string
  headerText: string
  isOpen?: boolean
  quoteId: string
  closeModal: () => void
}

class InvoiceLogModal extends React.Component<IGlolbalInvoiceLogModalProps> {
  public async componentDidMount(): Promise<void> {
    await this.getInvoiceLogData()
  }

  private setPaginationOffsetData = () => {
    const { page, totalItems, pageSize } = this.state
    const firstRecordIndex = (page - 1) * pageSize + 1
    const lastRecordIndex = page * pageSize < totalItems ? page * pageSize : totalItems
    this.setState({
      firstRecordIndex: firstRecordIndex,
      lastRecordIndex: lastRecordIndex,
    })
  }

  private getInvoiceLogData = async () => {
    const { page, pageSize } = this.state
    const invoiceId = this.props.invoiceId
    const data: InvoiceLogSearchRequest = {
      invoiceId,
      page,
      pageSize,
    }

    await GetInvoiceLogByInvoiceId(this.props.quoteId, data).then(
      async (response: any) => {
        this.setState(
          {
            gridData: response.items,
            pageCount: response.totalPages,
            totalItems: response.totalItems,
          },
          () => {
            this.setPaginationOffsetData()
          }
        )
      }
    )

    this.setState({ isLoading: false })
  }

  private handlePageChange = (event: any) => {
    const page = event.selected
    this.setState({ page: page + 1 }, () => this.getInvoiceLogData())
  }

  private isGridDataEmpty = () => {
    return this.state.totalItems === 0
  }

  private mapLogGridData = () => {
    if (this.isGridDataEmpty()) {
      return (
        <TableRow className="gridNoPadding" key={1}>
          <TableCell colSpan={10} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    } else {
      return this.state.gridData.map((record, index) => (
        <TableRow className="gridPadding" key={index}>
          <TableCell align="right">
            {moment(record.sentDate).format('MM/DD/YYYY')}
          </TableCell>
          <TableCell align="right">
            {moment(record.createdDate).format('MM/DD/YYYY')}
          </TableCell>
          <TableCell>{record.createdByName}</TableCell>
          <TableCell align="right">
            {moment(record.updatedDate).format('MM/DD/YYYY')}
          </TableCell>
          <TableCell>{record.updatedByName}</TableCell>
          <TableCell align="right">
            {record.invoiceTotal != undefined
              ? formatCurrency(record.invoiceTotal.toString())
              : ''}
          </TableCell>
          <TableCell align="right">
            {record.ucTotal != undefined ? formatCurrency(record.ucTotal.toString()) : ''}
          </TableCell>
          <TableCell align="right">
            {record.totalAmountPaid != undefined
              ? formatCurrency(record.totalAmountPaid.toString())
              : ''}
          </TableCell>
          <TableCell>
            <pre>{record.notes ?? ''}</pre>
          </TableCell>
        </TableRow>
      ))
    }
  }

  private paginationInfo = () => {
    if (!this.isGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {this.state.firstRecordIndex} to {this.state.lastRecordIndex} of{' '}
            {this.state.totalItems} entries
          </Typography>
        </div>
      )
    } else {
      return <></>
    }
  }

  public state = {
    gridData: new pagedList().items as InvoiceLogDTO[],
    page: 1,
    pageCount: 1,
    firstRecordIndex: 0,
    lastRecordIndex: 0,
    totalItems: 0,
    pageSize: 5,
    invoiceId: this.props.invoiceId,
    isLoading: true,
  }

  public render() {
    return (
      <>
        <Dialog
          key={this.props.quoteId}
          fullWidth={true}
          maxWidth="lg"
          open={this.props.isOpen!}
        >
          <DialogTitle style={{ padding: 0 }}>
            <ResponsiveAppBar
              title={'Invoice Log - ' + this.props.headerText}
              pageIcon={<Receipt />}
              isModal={true}
            />
          </DialogTitle>
          <DialogContent style={{ padding: '12px 12px 8px' }}>
            {this.state.isLoading && (
              <div
                style={{
                  position: 'fixed',
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  cursor: 'pointer',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: '1200',
                }}
              >
                <CircularProgress size={100} />
              </div>
            )}
            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignContent="center"
              alignItems="flex-start"
              wrap="wrap"
            >
              <Grid item xs={12} style={{ minWidth: '750px' }}>
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow className="gridPadding">
                      <TableCell align="right">Sent Date</TableCell>
                      <TableCell align="right">Created Date</TableCell>
                      <TableCell>Created By</TableCell>
                      <TableCell align="right">Updated Date</TableCell>
                      <TableCell>Updated By</TableCell>
                      <TableCell align="right">Invoice Total</TableCell>
                      <TableCell align="right">U&C Total</TableCell>
                      <TableCell align="right">Amount Paid</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody style={{ width: '100%' }}>{this.mapLogGridData()}</TableBody>
                </Table>
                <div className="pagination-row">
                  {this.paginationInfo()}
                  <ReactPaginate
                    previousLabel={'<'}
                    nextLabel={'>'}
                    onPageChange={this.handlePageChange}
                    forcePage={this.state.page - 1}
                    pageCount={this.state.pageCount}
                    containerClassName={'pagination'}
                    activeClassName={'active'}
                    //@ts-ignore
                    renderOnZeroPageCount={null}
                  />
                </div>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.props.closeModal} data-cy="close">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }
}

export default InvoiceLogModal
